import React, { useEffect, useState, useContext } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import brLocale from 'date-fns/locale/pt-BR';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { Button, TextField, Box, Typography, useTheme, alpha } from '@mui/material';
import api from '../../services/api';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { AuthContext } from "../../context/Auth/AuthContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

export const ChatsUser = () => {
    const theme = useTheme();
    const [initialDate, setInitialDate] = useState(new Date());
    const [finalDate, setFinalDate] = useState(new Date());
    const [ticketsData, setTicketsData] = useState({ data: [] });
    const { user } = useContext(AuthContext);

    const companyId = user.companyId;

    useEffect(() => {
        if (companyId) {
            handleGetTicketsInformation();
        }
    }, [companyId]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: { display: false },
            tooltip: {
                backgroundColor: theme.palette.background.paper,
                titleColor: theme.palette.text.primary,
                bodyColor: theme.palette.text.secondary,
                borderColor: theme.palette.divider,
                borderWidth: 1,
                cornerRadius: 12,
                padding: 12,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
            },
            datalabels: {
                display: true,
                anchor: 'end',
                align: "top",
                color: theme.palette.text.primary,
                font: { size: 12, weight: "bold" },
                offset: 4
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: theme.palette.divider, drawBorder: false },
                ticks: { 
                    color: theme.palette.text.secondary,
                    font: { size: 11 }
                }
            },
            x: {
                grid: { display: false },
                ticks: { 
                    color: theme.palette.text.secondary,
                    font: { size: 11 }
                }
            }
        }
    };

    const dataCharts = {
        labels: ticketsData && ticketsData?.data.length > 0 && ticketsData?.data.map((item) => item.nome),
        datasets: [{
            data: ticketsData?.data.length > 0 && ticketsData?.data.map((item) => item.quantidade),
            backgroundColor: alpha(theme.palette.primary.main, 0.8),
            borderRadius: 8,
            barThickness: 24,
            maxBarThickness: 32,
            hoverBackgroundColor: theme.palette.primary.main
        }],
    };

    const handleGetTicketsInformation = async () => {
        try {
            const { data } = await api.get(`/dashboard/ticketsUsers?initialDate=${format(initialDate, 'yyyy-MM-dd')}&finalDate=${format(finalDate, 'yyyy-MM-dd')}&companyId=${companyId}`);
            setTicketsData(data);
        } catch (error) {
            toast.error('Erro ao buscar informações dos tickets');
        }
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 1.5 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                    <DatePicker
                        value={initialDate}
                        onChange={(newValue) => setInitialDate(newValue)}
                        label={i18n.t("dashboard.date.initialDate")}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                size="small" 
                                sx={{ 
                                    width: 160, 
                                    "& .MuiOutlinedInput-root": { 
                                        borderRadius: "12px",
                                        backgroundColor: alpha(theme.palette.background.default, 0.5)
                                    } 
                                }} 
                            />
                        )}
                    />
                    <DatePicker
                        value={finalDate}
                        onChange={(newValue) => setFinalDate(newValue)}
                        label={i18n.t("dashboard.date.finalDate")}
                        renderInput={(params) => (
                            <TextField 
                                {...params} 
                                size="small" 
                                sx={{ 
                                    width: 160, 
                                    "& .MuiOutlinedInput-root": { 
                                        borderRadius: "12px",
                                        backgroundColor: alpha(theme.palette.background.default, 0.5)
                                    } 
                                }} 
                            />
                        )}
                    />
                </LocalizationProvider>
                <Button
                    onClick={handleGetTicketsInformation}
                    variant='contained'
                    color="primary"
                    sx={{ 
                        borderRadius: "50px", 
                        px: 4, 
                        fontWeight: 'bold',
                        textTransform: "none",
                        boxShadow: "none",
                        height: "40px",
                        "&:hover": { boxShadow: theme.shadows[2] }
                    }}
                >
                    Filtrar
                </Button>
            </Box>

            <Box sx={{ 
                height: 350, 
                width: '100%', 
                p: 2, 
                borderRadius: "16px",
                bgcolor: alpha(theme.palette.background.default, 0.3),
                border: `1px solid ${theme.palette.divider}`
            }}>
                <Bar options={options} data={dataCharts} />
            </Box>
        </Box>
    );
}